// Standalone, read-only, public athlete / coach profile page rendered for share
// links (`/p/:token`). It mounts OUTSIDE the auth provider (see `main.tsx`), so
// a visitor never sees a login screen — the opaque profile token in the URL is
// the only capability. A coach's page is public by default; a plain athlete's
// is served only once the company enables it (otherwise this shows a friendly
// "not available" state). The server returns a narrow projection with no
// personal number, no exact birth date and no attendance figures.
//
// A coach's page also shows the weekly schedule of the classes they run — the
// same interactive planner as the club home page (`ClubWeekCalendar`), fed
// with only their coached classes. The content is already public on the
// club's own page; this just answers "when does this coach teach?" in place.
//
// Visual style matches the public spectator view: a gradient hero over frosted,
// glowing cards (see `publicStyles`).

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupsIcon from '@mui/icons-material/Groups';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { PublicClubTraining } from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { LanguageSelector } from '../LanguageSelector';
import { PhotoGallery } from '../PhotoGallery';
import { ClubWeekCalendar } from './ClubWeekCalendar';
import { usePublicProfile } from './usePublicProfile';
import { publicClubPath } from './clubSharePath';
import { ACCENT_GRADIENT, HERO_GRADIENT, glowCard, rise } from './publicStyles';

interface PublicProfileViewProps {
  profileToken: string;
}

export function PublicProfileView({ profileToken }: PublicProfileViewProps) {
  const { t } = useTranslation();
  const { profile, status, reload } = usePublicProfile(profileToken);

  // The classes this person coaches, shaped for the week planner. Coaches are
  // left empty on purpose: every card is this coach's own class, so naming
  // them on each card would only repeat the page's title.
  const coachedTraining = useMemo<PublicClubTraining[]>(
    () =>
      (profile?.classes ?? [])
        .filter((cls) => cls.role === 'COACH' && cls.schedule.length > 0)
        .map((cls) => ({
          name: cls.name,
          description: cls.description,
          schedule: cls.schedule,
          audience: cls.audience,
          coaches: [],
        })),
    [profile],
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundImage: HERO_GRADIENT }}>
        <Container maxWidth="sm" sx={{ pt: 2, pb: 1 }}>
          <Stack direction="row" justifyContent="flex-end">
            <LanguageSelector />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ pb: 6 }}>
        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {(status === 'notfound' || status === 'error') && (
          <Paper sx={{ ...glowCard(), p: 4, mt: 4, textAlign: 'center' }}>
            <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {t(status === 'notfound' ? 'profile.notFound.title' : 'profile.error.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(status === 'notfound' ? 'profile.notFound.body' : 'profile.error.body')}
            </Typography>
            {status === 'error' && (
              <Button startIcon={<RefreshIcon />} onClick={reload}>
                {t('profile.retry')}
              </Button>
            )}
          </Paper>
        )}

        {status === 'ready' && profile && (
          <Stack spacing={3} sx={{ mt: 2, animation: `${rise} .4s ease both` }}>
            {/* Hero: avatar, name, role, age, club. */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={profile.photoUrls[0] ?? undefined}
                alt={profile.displayName}
                sx={{
                  width: 88,
                  height: 88,
                  backgroundImage: profile.photoUrls[0] ? undefined : ACCENT_GRADIENT,
                }}
              >
                {profile.isCoach ? (
                  <SportsIcon sx={{ fontSize: 40 }} />
                ) : (
                  <DirectionsRunIcon sx={{ fontSize: 40 }} />
                )}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {profile.displayName}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    color={profile.isCoach ? 'secondary' : 'default'}
                    label={t(profile.isCoach ? 'profile.coach' : 'profile.athlete')}
                  />
                  {profile.age !== null && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('profile.ageYears', { count: profile.age })}
                    />
                  )}
                  {profile.clubName &&
                    (profile.clubHomeToken ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        clickable
                        component="a"
                        href={publicClubPath(profile.clubHomeToken)}
                        label={profile.clubName}
                      />
                    ) : (
                      <Chip size="small" variant="outlined" label={profile.clubName} />
                    ))}
                </Stack>
              </Box>
            </Stack>

            {/* Bio (coach description). */}
            {profile.bio && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('profile.about')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {profile.bio}
                </Typography>
              </Paper>
            )}

            {/* Photos. */}
            <Paper sx={{ ...glowCard(), p: 3 }}>
              <Typography variant="overline" color="text.secondary">
                {t('profile.photos')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <PhotoGallery
                  urls={profile.photoUrls}
                  alt={profile.displayName}
                  emptyLabel={t('profile.noPhotos')}
                  labels={{
                    close: t('gallery.close'),
                    download: t('gallery.download'),
                    previous: t('gallery.previous'),
                    next: t('gallery.next'),
                  }}
                />
              </Box>
            </Paper>

            {/* Sports. */}
            {profile.sports.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('profile.sports')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {profile.sports.map((sport) => (
                    <Chip key={sport.sportId} color="info" variant="outlined" label={sport.sportName} />
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Clubs & teams. */}
            {profile.teams.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('profile.teams')}
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {profile.teams.map((team, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                      <GroupsIcon fontSize="small" color="disabled" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {team.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip size="small" variant="outlined" label={team.sportName} />
                          {team.teamRole && (
                            <Chip size="small" variant="outlined" label={team.teamRole} />
                          )}
                          {team.tier && (
                            <Chip size="small" variant="outlined" label={team.tier} />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Training classes (enrolled + coached). */}
            {profile.classes.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('profile.classes')}
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {profile.classes.map((cls, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                      <FitnessCenterIcon fontSize="small" color="disabled" />
                      <Typography variant="subtitle2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                        {cls.name}
                      </Typography>
                      {cls.audience !== 'ALL' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={cls.audience === 'KIDS' ? 'warning' : 'info'}
                          label={t(`club.schedule.audience.${cls.audience}`)}
                        />
                      )}
                      <Chip
                        size="small"
                        color={cls.role === 'COACH' ? 'secondary' : 'default'}
                        variant="outlined"
                        label={t(`profile.classRole.${cls.role}`)}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* The weekly schedule of the classes this coach runs — the same
                planner the club home page uses, scoped to their classes. Kept
                as the page's closing section: the identity content reads
                first, the planner is the deep-dive at the bottom. */}
            {coachedTraining.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('profile.schedule')}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <ClubWeekCalendar training={coachedTraining} />
                </Box>
              </Paper>
            )}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
